import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

const VerseCleanupTool = () => {
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('reference');
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  useEffect(() => {
    fetchVerses();
  }, []);

  const fetchVerses = async () => {
    try {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      const verseList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVerses(verseList);
    } catch (error) {
      console.error('Error fetching verses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (verseId, reference) => {
    if (window.confirm(`Are you sure you want to delete verse: ${reference}?`)) {
      try {
        await deleteDoc(doc(db, 'verses', verseId));
        setVerses(verses.filter(v => v.id !== verseId));
      } catch (error) {
        console.error('Error deleting verse:', error);
        alert('Failed to delete verse');
      }
    }
  };

  const findDuplicates = () => {
    const referenceCount = verses.reduce((acc, verse) => {
      acc[verse.reference] = (acc[verse.reference] || 0) + 1;
      return acc;
    }, {});
    
    const duplicateVerses = verses.filter(verse => referenceCount[verse.reference] > 1);
    const uniqueDuplicateReferences = new Set(duplicateVerses.map(v => v.reference));
    
    return {
      verses: duplicateVerses,
      uniqueCount: uniqueDuplicateReferences.size,
      totalInstances: duplicateVerses.length
    };
  };

  const filteredVerses = verses
    .filter(verse => {
      if (!filter) return true;
      return verse.reference?.toLowerCase().includes(filter.toLowerCase()) ||
             verse.text?.toLowerCase().includes(filter.toLowerCase());
    })
    .filter(verse => {
      if (!showDuplicatesOnly) return true;
      const duplicates = findDuplicates();
      return duplicates.verses.some(d => d.reference === verse.reference);
    })
    .sort((a, b) => {
      if (sortBy === 'reference') {
        return a.reference?.localeCompare(b.reference);
      }
      return a.id?.localeCompare(b.id);
    });

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-6">Verse Cleanup Tool</h1>
        
        {/* Controls */}
        <div className="flex gap-4 mb-6">
          <input
            type="text"
            placeholder="Filter by reference or text..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 p-2 border rounded-md"
          />
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="reference">Sort by Reference</option>
            <option value="id">Sort by ID</option>
          </select>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showDuplicatesOnly}
              onChange={(e) => setShowDuplicatesOnly(e.target.checked)}
              className="mr-2"
            />
            Show Duplicates Only
          </label>
        </div>

        {/* Verses List */}
        {loading ? (
          <div className="text-center">Loading verses...</div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-2">
              {showDuplicatesOnly ? (
                <>
                  Found {findDuplicates().uniqueCount} verses with duplicates
                  ({findDuplicates().totalInstances} total instances)
                </>
              ) : (
                `Showing ${filteredVerses.length} total verses`
              )}
            </div>
            
            {filteredVerses.map(verse => (
              <div key={verse.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium">{verse.reference}</h3>
                      <span className="text-xs text-gray-500">ID: {verse.id}</span>
                    </div>
                    <p className="text-sm text-gray-600">{verse.text}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {verse.growthArea && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                          {verse.growthArea}
                        </span>
                      )}
                      {verse.devTopic && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          {verse.devTopic}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(verse.id, verse.reference)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerseCleanupTool; 